import { Progress, Stack, Text } from '@mantine/core';
import type { BillingDetail } from 'src/secret-menu/billing/hooks/useBilling';
import {
  lineOf,
  type MonthRevenue,
  type RevenueOverview,
} from 'src/secret-menu/billing/hooks/useRevenue';
import { formatPercent, percentOfStated } from '../overview/revenue';
import { compare, lastMonthOf } from './history';
import { ChangeArrow, formatRevenue, formatSignedUnits, formatUnits, PerYear, Stat } from './ui';

/**
 * The revenue figures the Overview headline and the Revenue tab both show, so they read the same
 * wherever they appear. Each is today's figure, whichever month the Overview has selected.
 */

type Props = { overview: RevenueOverview; history: readonly MonthRevenue[]; today: Date };

const NO_SNAPSHOT = 'No previous month to compare yet';

/** "+12 since last month", or "No change since last month": the plain difference beside the chip. */
export function sinceText(current: number, previous: number, when: string): string {
  if (current === previous) return `No change since ${when}`;
  return `${formatSignedUnits(current - previous)} since ${when}`;
}

/** "1 Nov 2026": the day the figures stand at. */
export const formatDay = (day: Date) =>
  day.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export function MrrStat({
  overview,
  history,
  today,
  size,
  aside,
}: Props & { size?: 'headline' | 'default'; aside?: React.ReactNode }) {
  const adviserFirms =
    lineOf(overview.today, 'ADVISER_PER_UNIT').mrrPence +
    lineOf(overview.today, 'ADVISER_FIXED').mrrPence;
  // The strategic partner has no history, so last month counts it as it stands today.
  const lastMonth = lastMonthOf(history, today);
  const lastMonthTotal =
    lastMonth === null ? null : lastMonth.adviserMrrPence + overview.monumentToday.totalPence;

  return (
    <Stat
      label="Monthly recurring revenue"
      caption={`As of ${formatDay(today)}`}
      value={formatRevenue(overview.today.mrrPence)}
      change={
        lastMonthTotal === null ? undefined : (
          <ChangeArrow current={overview.today.mrrPence} previous={lastMonthTotal} period="MoM" />
        )
      }
      rows={[
        { label: 'Adviser firms', value: formatRevenue(adviserFirms) },
        { label: 'Strategic partner', value: formatRevenue(overview.monumentToday.totalPence) },
      ]}
      detail={<PerYear pence={overview.today.arrPence} />}
      hint="monthlyRecurringRevenue"
      size={size}
      aside={aside}
    />
  );
}

/**
 * Billed families against the families firms say they have, across paying firms that have stated
 * a figure; a firm that has not said is left out rather than counted as nothing loaded.
 */
function StatedFamiliesBar({ firms }: { firms: readonly BillingDetail[] }) {
  const stated = firms.filter(
    firm => firm.pricingCategory !== 'TRIAL' && firm.statedBillableUnits !== null,
  );
  const statedFamilies = stated.reduce((total, firm) => total + (firm.statedBillableUnits ?? 0), 0);
  const billed = stated.reduce((total, firm) => total + firm.activeBillableUnitCount, 0);
  const percent = percentOfStated(billed, statedFamilies);
  if (percent === null) {
    return (
      <Text size="textXs" c="dimmed">
        No stated families to compare with
      </Text>
    );
  }
  return (
    <Stack gap={4}>
      <Progress value={Math.min(percent, 100)} color="green" size="sm" />
      <Text size="textXs" c="dimmed">
        {formatPercent(percent)} of {formatUnits(statedFamilies)} stated families billed
      </Text>
    </Stack>
  );
}

export function BilledUnitsStat({
  overview,
  history,
  today,
  firms,
}: Props & { firms: readonly BillingDetail[] }) {
  const units = compare(overview.billedUnits, lastMonthOf(history, today)?.billedUnits ?? null);
  return (
    <Stat
      label="Billable units"
      caption={`As of ${formatDay(today)}`}
      value={formatUnits(units.current)}
      change={
        units.previous === null ? undefined : (
          <ChangeArrow current={units.current} previous={units.previous} period="MoM" />
        )
      }
      detail={
        units.previous === null
          ? NO_SNAPSHOT
          : sinceText(units.current, units.previous, 'last month')
      }
      hint="billedUnits"
      footer={<StatedFamiliesBar firms={firms} />}
    />
  );
}

export function FullAdoptionStat({ overview }: Pick<Props, 'overview'>) {
  const adviserFirms =
    lineOf(overview.fullAdoption, 'ADVISER_PER_UNIT').mrrPence +
    lineOf(overview.fullAdoption, 'ADVISER_FIXED').mrrPence;
  return (
    <Stat
      label="Monthly recurring revenue"
      caption="At full adoption"
      value={formatRevenue(overview.fullAdoption.mrrPence)}
      change={
        <ChangeArrow current={overview.fullAdoption.mrrPence} previous={overview.today.mrrPence} />
      }
      rows={[
        { label: 'Adviser firms', value: formatRevenue(adviserFirms) },
        {
          label: 'Strategic partner',
          value: formatRevenue(overview.monumentFullAdoption.totalPence),
        },
      ]}
      detail={<PerYear pence={overview.fullAdoption.arrPence} />}
      hint="fullAdoption"
    />
  );
}

/** Free-trial firms: what they would bill on converting, and at full adoption. */
export function PipelineStat({ overview, history, today }: Props) {
  const pipeline = compare(
    overview.contractedNotBillingPence,
    lastMonthOf(history, today)?.pipelinePence ?? null,
  );
  const trials = overview.pipeline.length;
  return (
    <Stat
      label="Pipeline (free trials)"
      caption="A month immediately on conversion"
      value={formatRevenue(pipeline.current)}
      change={
        pipeline.previous === null ? undefined : (
          <ChangeArrow current={pipeline.current} previous={pipeline.previous} period="MoM" />
        )
      }
      rows={[
        { label: 'Firms on a free trial', value: formatUnits(trials) },
        {
          label: 'At full adoption',
          value: `${formatRevenue(overview.pipelineFullAdoptionPence)} a month`,
        },
      ]}
      detail={
        <PerYear pence={overview.pipelineFullAdoptionPence * 12} suffix=" at full adoption" />
      }
      hint="pipeline"
    />
  );
}
